import Card from "components/card";
import Loader from "components/loader";
import useStats from "hooks/useStats";
import { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { states } from "utils/Tables";
import { DASHBOARD_COLORS } from "utils/dashboardColors";

const TOP_N = 10;

const agregateDataByProvince = (data) => {
  const provinceMap = {};

  states.forEach(({ name, value }) => {
    provinceMap[value] = { province: name, postulant: 0, institution: 0, total: 0 };
  });

  data.forEach((info) => {
    if (provinceMap[info?.provincia]) {
      provinceMap[info?.provincia].postulant = parseInt(info?.total_postulantes || 0);
      provinceMap[info?.provincia].institution = parseInt(info?.total_instituciones || 0);
      provinceMap[info?.provincia].total =
        provinceMap[info?.provincia].postulant + provinceMap[info?.provincia].institution;
    }
  });

  return Object.values(provinceMap);
};

const ProvincesChart = () => {
  const { isLoading, fetchData } = useStats("usuarios-provincia");
  const [provincias, setProvincias] = useState([]);

  const fetchProvincias = async () => {
    const data = await fetchData(true);
    const aggregated = agregateDataByProvince(data)
      .filter((province) => province.total !== 0)
      .sort((a, b) => b.total - a.total)
      .slice(0, TOP_N);

    setProvincias(aggregated);
  };

  useEffect(() => {
    fetchProvincias();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chartSeries = useMemo(
    () => [
      { name: "Postulantes", data: provincias.map((p) => p.postulant) },
      { name: "Instituciones", data: provincias.map((p) => p.institution) },
    ],
    [provincias]
  );

  const chartOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        stacked: false,
        toolbar: { show: false },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: "60%",
        },
      },
      colors: [DASHBOARD_COLORS.postulantes, DASHBOARD_COLORS.instituciones],
      dataLabels: { enabled: false },
      xaxis: {
        categories: provincias.map((p) => p.province),
        labels: { style: { colors: "#A3AED0", fontSize: "12px" } },
      },
      yaxis: {
        labels: { style: { colors: "#A3AED0", fontSize: "12px" } },
      },
      legend: { show: true, position: "top" },
      grid: { show: false },
      tooltip: { theme: "dark" },
    }),
    [provincias]
  );

  return (
    <Card extra="w-full h-[460px] px-6 py-4">
      <div className="text-xl font-bold text-navy-700 dark:text-white mb-2">
        Usuarios por región (top 10)
      </div>

      <div className="h-[380px] w-full">
        {isLoading ? (
          <Loader />
        ) : provincias.length === 0 ? (
          <p className="text-sm text-gray-600">No hay datos disponibles</p>
        ) : (
          <Chart options={chartOptions} series={chartSeries} type="bar" width="100%" height="100%" />
        )}
      </div>
    </Card>
  );
};

export default ProvincesChart;
